import { Injectable, inject } from "@angular/core";
import { Observable, of, TimeoutError, combineLatest, from, EMPTY } from "rxjs";
import { Store } from "@ngxs/store";
import { take, switchMap, map, filter, timeout, catchError, first } from "rxjs/operators";
import {
	HttpEvent,
	HttpHandler,
	HttpHeaders,
	HttpInterceptor,
	HttpRequest,
	HttpErrorResponse,
	HttpContextToken,
} from "@angular/common/http";
import { Router } from "@angular/router";

import { AuthenticationState } from "./state/authentication.state";
import { AuthenticateServiceUser, TokenExpired } from "./state/authentication.actions";
import { OnSiteAuthService } from "./AuthService";

export const SERVICEUSER_TOKEN = new HttpContextToken<boolean>(() => false);

@Injectable({
	providedIn: "root",
})
export class AuthInterceptor implements HttpInterceptor {
	private store = inject(Store);
	private authService = inject(OnSiteAuthService);
	private router = inject(Router);

	private serviceUser$ = this.store.select(AuthenticationState.serviceUser);
	private user$ = this.store.select(AuthenticationState.user);

	intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
		let reqHeaders = req.headers;
		const isServiceUser = req.context.get(SERVICEUSER_TOKEN);

		// First check if both serviceUser and user are null
		return combineLatest([this.serviceUser$, this.user$]).pipe(
			first(),
			switchMap(([serviceUser, user]) => {
				// If both are null, we are in an invalid state and we need to unpair the doorsign
				if (!serviceUser && !user) {
					console.log("Both serviceUser and user are null, redirecting to reset route");
					this.router.navigate(["/reset"]);
					// Cancel the request by returning EMPTY
					return EMPTY;
				}

				// Continue with the normal authentication flow
				return this.createAuthHeaders(isServiceUser).pipe(
					switchMap((authHeaders) => {
						if (!authHeaders) {
							return next.handle(req);
						}

						reqHeaders = this.applyHeaders(reqHeaders, authHeaders);
						return next.handle(
							req.clone({
								headers: reqHeaders,
							}),
						);
					}),
				);
			}),
		);
	}

	private applyHeaders(target: HttpHeaders, source: HttpHeaders): HttpHeaders {
		let result = target;
		source.keys().forEach((key) => {
			const value = source.get(key);
			if (value) {
				result = result.set(key, value);
			}
		});
		return result;
	}

	private createAuthHeaders(useServiceUser: boolean): Observable<HttpHeaders | null> {
		if (useServiceUser) {
			return this.serviceUser$.pipe(
				take(1),
				switchMap((serviceUser) => {
					if (serviceUser && !serviceUser.expired) {
						return of(this.authService.createAuthorizationHeader(serviceUser?.access_token));
					}

					// Dispatch the action to authenticate service user and wait for completion
					return this.store.dispatch(new AuthenticateServiceUser()).pipe(
						// First wait for the action to complete
						// Then subscribe to the service user selector and wait for a non-expired token
						switchMap(() =>
							this.serviceUser$.pipe(
								// Skip any null values or expired tokens
								// This ensures we wait until the authentication process completes and state is updated
								filter((user) => !!user && !user.expired),
								take(1), // Take only the first valid token
								// Set a reasonable timeout for authentication
								timeout(10000), // 10 second timeout
								map((validToken) => {
									if (!validToken) return null;
									return this.authService.createAuthorizationHeader(validToken.access_token);
								}),
								catchError((error) => {
									// Provide context about where the error occurred
									if (error instanceof TimeoutError) {
										console.error(
											"Authentication timeout in HTTP interceptor - service user authentication took too long",
										);
									} else {
										console.error("Authentication error in HTTP interceptor:", error);
									}

									// Check if we need to trigger a login redirect
									if (
										error instanceof HttpErrorResponse &&
										(error.status === 401 || error.status === 403)
									) {
										// Dispatch TokenExpired to trigger login redirect
										this.store.dispatch(new TokenExpired());
									}

									// Return null to allow the request to proceed without auth headers
									// This lets the backend return appropriate 401 errors
									return of(null);
								}),
							),
						),
					);
				}),
			);
		}

		// For user token, wait until it's available
		return this.user$.pipe(
			filter((token) => !!token), // Wait until token is available
			take(1),
			map((user) => {
				if (!user) {
					return null;
				}
				return this.authService.createAuthorizationHeader(user.access_token);
			}),
		);
	}
}
